import { DefaultError, InfiniteData, QueryKey, useInfiniteQuery, UseInfiniteQueryOptions } from '@tanstack/react-query';
import { useScrollBreakpoint } from 'hooks';
import { last } from 'lodash';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import ICursorable from 'types/Cursorable';
import { Nullable } from 'types/util/Nullable';
import { Optional } from 'types/util/Optional';
import { graphqlPagingLoopShouldBreak } from 'utils/graphQlUtils';
import { convertGetDataToReactQueryPager, getGraphqlNextPageParam } from 'utils/reactQueryUtils';
import { useClearReactQueryKey } from '../useClearReactQueryKey/useClearReactQueryKey';

export type ReactQueryLazyLoadingPassableOptions<T> = Omit<
  UseInfiniteQueryOptions<T, DefaultError, InfiniteData<T>, any, QueryKey, Nullable<string>>,
  'queryKey' | 'queryFn' | 'getNextPageParam' | 'initialPageParam'
>;

export function useReactQueryLazyLoading<T extends ICursorable>(
  scroller: Optional<HTMLElement>,
  queryKey: QueryKey,
  getData: (after: string | null, pageSize: number) => Promise<T[]>,
  pageSize: number = 50,
  options?: ReactQueryLazyLoadingPassableOptions<T[]>
) {
  const { data, ...rest } = useInfiniteQuery({
    ...options,
    queryKey,
    queryFn: convertGetDataToReactQueryPager<T[]>(async (after) => getData(after ?? null, pageSize)),
    initialPageParam: null,
    getNextPageParam: getGraphqlNextPageParam(pageSize),
  });

  const infiniteScroll = useMemo(
    () => ({
      ...rest,
      data: data?.pages ? data?.pages?.flat() : null,
    }),
    [data?.pages, rest]
  );

  const fetchNextPage = infiniteScroll.fetchNextPage;
  const hasNextPage = infiniteScroll.hasNextPage;

  const fetching = useRef(false);
  const getNextPage = useCallback(async () => {
    if (!fetching.current && hasNextPage) {
      fetching.current = true;
      await fetchNextPage();
      fetching.current = false;
    }
  }, [hasNextPage, fetchNextPage]);

  const loadAll = useCallback(async () => {
    fetching.current = true;
    let data = infiniteScroll.data ?? [];

    while (infiniteScroll.hasNextPage) {
      // Fetch next page will have all pages attached
      const result = await infiniteScroll.fetchNextPage();
      const lastPage = last(result?.data?.pages);

      if (graphqlPagingLoopShouldBreak(lastPage, pageSize)) {
        data = result?.data?.pages?.flat() ?? [];
        break;
      }
    }

    fetching.current = false;
    return data;
  }, [infiniteScroll, pageSize]);

  useScrollBreakpoint(scroller, getNextPage);

  const isEnabled = !options || options.enabled !== false;

  // Used for when the request payload doesn't result in a render that is scrollable so there's no way to trigger getting more data
  useEffect(() => {
    if (!isEnabled) {
      return;
    }

    function getPageIfUnscrollable() {
      if (scroller) {
        const scrollerIsScrollable = scroller.scrollHeight > scroller.clientHeight;
        if (!scrollerIsScrollable && !fetching.current && infiniteScroll.hasNextPage) {
          getNextPage();
        }
      }
    }

    getPageIfUnscrollable();
    window.addEventListener('resize', getPageIfUnscrollable);

    return () => {
      window.removeEventListener('resize', getPageIfUnscrollable);
    };
  }, [scroller, infiniteScroll, getNextPage, isEnabled]);

  const isLoading = infiniteScroll.isLoading || fetching.current;

  return {
    ...infiniteScroll,
    isLoading,
    loadAll,
    loadedAll: !isLoading && !infiniteScroll.hasNextPage,
    clearData: useClearReactQueryKey(queryKey),
  };
}
